import React from 'react';
import { Eye, EyeOff, Activity, Database, Terminal } from 'lucide-react';

interface WatchlistStatsProps {
    totalItems: number;
    enabledItems: number;
    disabledItems: number;
    recentlyAdded: number;
    isLoading?: boolean;
}

const WatchlistStats: React.FC<WatchlistStatsProps> = ({
    totalItems,
    enabledItems,
    disabledItems,
    recentlyAdded,
    isLoading = false
}) => {
    if (isLoading) {
        return (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6 font-mono">
                {[...Array(4)].map((_, index) => (
                    <div
                        key={index}
                        className="bg-[#050505] border border-[#1A1D24] rounded-xl p-5 animate-pulse shadow-[0_0_20px_rgba(0,0,0,0.5)]"
                    >
                        <div className="flex justify-between items-start mb-4">
                            <div className="h-4 bg-[#1A1D24] rounded w-1/2"></div>
                            <div className="h-8 w-8 bg-[#1A1D24] rounded-lg"></div>
                        </div>
                        <div className="h-8 bg-[#1A1D24] rounded w-1/3 mt-2"></div>
                    </div>
                ))}
            </div>
        );
    }

    const enabledRate = totalItems > 0 ? Math.round((enabledItems / totalItems) * 100) : 0;

    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6 font-mono">

            {/* Total Monitored Card */}
            <div className="bg-[#050505] border border-[#1A1D24] border-t-2 border-t-[#4da760] rounded-xl p-5 shadow-[0_0_20px_rgba(0,0,0,0.5)] relative overflow-hidden group hover:bg-[#0A0B0E] transition-colors">
                <div className="flex items-start justify-between">
                    <div>
                        <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">
                            Matrix Load
                        </div>
                        <div className="text-3xl font-bold text-[#4da760] tracking-tight">
                            {totalItems}
                        </div>
                    </div>
                    <div className="w-10 h-10 bg-[#4da760]/10 border border-[#4da760]/30 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                        <Database className="h-5 w-5 text-[#4da760]" />
                    </div>
                </div>
            </div>

            {/* Enabled / Active Card */}
            <div className="bg-[#050505] border border-[#1A1D24] border-t-2 border-t-[#50A970] rounded-xl p-5 shadow-[0_0_20px_rgba(0,0,0,0.5)] relative overflow-hidden group hover:bg-[#0A0B0E] transition-colors">
                <div className="flex items-start justify-between">
                    <div>
                        <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">
                            Live Intercepts
                        </div>
                        <div className="text-3xl font-bold text-[#50A970] tracking-tight">
                            {enabledItems}
                        </div>
                    </div>
                    <div className="w-10 h-10 bg-[#50A970]/10 border border-[#50A970]/30 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                        <Eye className="h-5 w-5 text-[#50A970]" />
                    </div>
                </div>
                <div className="mt-3 flex items-center gap-2">
                    <div className="flex-1 h-1 bg-[#1A1D24] rounded-full overflow-hidden">
                        <div
                            className="h-full bg-[#50A970] transition-all duration-1000 ease-out"
                            style={{ width: `${enabledRate}%` }}
                        />
                    </div>
                    <span className="text-[10px] font-bold text-[#50A970] tracking-widest">
                        {enabledRate}%
                    </span>
                </div>
            </div>

            {/* Disabled Card */}
            <div className="bg-[#050505] border border-[#1A1D24] border-t-2 border-t-slate-600 rounded-xl p-5 shadow-[0_0_20px_rgba(0,0,0,0.5)] relative overflow-hidden group hover:bg-[#0A0B0E] transition-colors">
                <div className="flex items-start justify-between">
                    <div>
                        <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">
                            Offline Nodes
                        </div>
                        <div className="text-3xl font-bold text-slate-400 tracking-tight">
                            {disabledItems}
                        </div>
                    </div>
                    <div className="w-10 h-10 bg-slate-800/20 border border-slate-700/30 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                        <EyeOff className="h-5 w-5 text-slate-500" />
                    </div>
                </div>
                <div className="mt-3 flex items-center gap-1.5">
                    <span className="text-[9px] text-slate-600 uppercase tracking-widest">
                        Monitoring suspended for {disabledItems} entities
                    </span>
                </div>
            </div>

            {/* Recently Added Card */}
            <div className="bg-[#050505] border border-[#1A1D24] border-t-2 border-t-[#00FFFF] rounded-xl p-5 shadow-[0_0_20px_rgba(0,0,0,0.5)] relative overflow-hidden group hover:bg-[#0A0B0E] transition-colors">
                <div className="flex items-start justify-between">
                    <div>
                        <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">
                            New Telemetry
                        </div>
                        <div className="text-3xl font-bold text-[#00FFFF] tracking-tight">
                            {recentlyAdded}
                        </div>
                    </div>
                    <div className="w-10 h-10 bg-[#00FFFF]/10 border border-[#00FFFF]/30 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                        <Activity className="h-5 w-5 text-[#00FFFF]" />
                    </div>
                </div>
                <div className="mt-3 flex items-center gap-1.5">
                    <Terminal className="h-3 w-3 text-[#00FFFF] animate-pulse" />
                    <span className="text-[10px] text-[#00FFFF] uppercase tracking-widest">
                        Active Ingestion: 7D Window
                    </span>
                </div>
            </div>

        </div>
    );
};

export default WatchlistStats;